import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { ArrowRight, Route } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useLearnerStart } from "@/hooks/useLearnerStart";
import { useCareerDna } from "@/hooks/useCareerDna";
import { useLearnerCountry } from "@/hooks/useLearnerCountry";
import { careerSwitcherRoadmap, isCareerSwitcher } from "@/lib/careerSwitcherRoadmap";
import { loadCareerIntel } from "@/lib/careerIntel";
import { resolveCareerIds } from "@/lib/certifications";
import { COUNTRY_LABEL } from "@/lib/learnerCountry";
import { RoadmapTimeline } from "./CareerRoadmap";
import { CountryRoadmap } from "./CountryRoadmap";

export default function CareerSwitcherRoadmap({ compact = false }: { compact?: boolean }) {
  const start = useLearnerStart();
  const { data: dna } = useCareerDna();
  const { country } = useLearnerCountry();
  const matches = dna?.matches.slice(0, 3) ?? [];
  const [selected, setSelected] = useState("");
  const target = matches.find(m => m.name === selected) ?? matches[0];
  const switcher = isCareerSwitcher(start);
  const { data: intel } = useQuery({
    queryKey: ["switcher-career-intel", target?.name],
    enabled: switcher && !compact && Boolean(target?.name),
    queryFn: () => target ? loadCareerIntel(target.name) : null,
    staleTime: 5 * 60_000,
  });
  const [ciId, setCiId] = useState<string | null>(null);
  useEffect(() => {
    let active = true;
    setCiId(null);
    if (switcher && !compact && target) {
      resolveCareerIds([target]).then(ids => { if (active) setCiId(ids[0] ?? null); }).catch(() => {});
    }
    return () => { active = false; };
  }, [switcher, compact, target?.id, target?.name]);
  if (!switcher) return null;

  if (compact) return (
    <section aria-labelledby="switcher-preview" className="flex flex-wrap items-center justify-between gap-4 border-y border-border py-5">
      <div className="space-y-1 max-w-xl">
        <h2 id="switcher-preview" className="font-display text-xl font-semibold">Your career-switch roadmap</h2>
        <p className="text-sm text-muted-foreground">Carry your experience forward. Explore a new direction, test the work and plan your transition.</p>
      </div>
      <Button asChild className="min-h-12 gap-2"><Link to="/next-steps#career-switch-roadmap">Explore your roadmap <ArrowRight className="h-4 w-4" aria-hidden /></Link></Button>
    </section>
  );

  return (
    <section id="career-switch-roadmap" aria-labelledby="switcher-title" className="space-y-4 scroll-mt-24">
      <header className="space-y-2">
        <p className="flex items-center gap-2 text-sm font-medium text-primary"><Route className="h-4 w-4" aria-hidden />Recommended · Career transition</p>
        <h2 id="switcher-title" className="font-display text-2xl font-semibold">Your career-switch roadmap</h2>
        <p className="max-w-2xl text-sm text-muted-foreground">A suggested 12-week exploration plan, followed by a move at your own pace. Your experience matters; new skills and entry requirements still need to be checked.</p>
        {typeof start?.yearsExperience === "number" && <p className="text-sm text-muted-foreground">Starting from your reported {start.yearsExperience} year{start.yearsExperience === 1 ? "" : "s"} of work experience. This does not assume experience in your new field.</p>}
      </header>
      {target ? (
        <div className="space-y-3">
          <label htmlFor="switcher-target" className="block text-sm font-medium">Direction to explore</label>
          <Select value={target.name} onValueChange={setSelected}>
            <SelectTrigger id="switcher-target" className="min-h-12 w-full sm:max-w-lg"><SelectValue /></SelectTrigger>
            <SelectContent>{matches.map(m => <SelectItem key={m.id || m.name} value={m.name}>{m.name}</SelectItem>)}</SelectContent>
          </Select>
          <p className="text-sm text-muted-foreground">{target.explanation ? `Why explore this today: ${target.explanation}` : "From your current Career DNA possibilities. Test this direction with work samples and practitioner feedback before deciding."}</p>
          {dna?.isLocalPreview && <p className="text-xs text-muted-foreground">These possibilities come from your latest assessment preview; your full report is still being prepared.</p>}
        </div>
      ) : (
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">Start with your goals and current work. Complete the assessment to connect this plan to your Career DNA possibilities.</p>
          <Button asChild className="min-h-12 gap-2"><Link to="/start">Discover your Career DNA <ArrowRight className="h-4 w-4" aria-hidden /></Link></Button>
        </div>
      )}
      <RoadmapTimeline stages={careerSwitcherRoadmap(intel)} />
      {ciId && <div className="space-y-3 border-t border-border pt-5">
        <h3 className="font-display text-lg font-semibold">Requirements and opportunities in {COUNTRY_LABEL[country]}</h3>
        <p className="text-sm text-muted-foreground">Country research describes the role, not your personal eligibility. Confirm the requirements that apply to your transition.</p>
        <CountryRoadmap ciId={ciId} country={country} audience="switcher" />
        <Button asChild variant="outline" className="min-h-12"><Link to={`/careers/${ciId}`}>Explore {target?.name}</Link></Button>
      </div>}
    </section>
  );
}